import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ICart, ICartSuccess } from '~shared/modules/cart/models';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { TrackingData } from '~shared/modules/shopping-cart-global/models/add-to-cart.model';
import { ShoppingCartError } from '../classes/shopping-cart-error.class';
import { DEFAULT_CONFIG } from '../constants/cart-service.constants';
import { IOfferLine } from '../models';
import { ICartConfig } from '../models/cart-config.model';
import { ICartService } from '../models/cart-service.model';
import { CartConfigToken } from '../tokens/cart-config.token';
import { CartCommonUtil } from '../utils/cart-common-utils';
import { handleError } from '../utils/handle-error';
import { ApiConfig } from '~shared/constants';
import { CartOfferLine } from '~shared/modules/shopping-cart-store';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FF_ADD_TO_CART_ON_APP_CART } from '~shared/modules/cart/constants/feature-flag.constants';
import { uuidv4 } from '~shared/utils/uuid.util';

const EMPTY_CART: IOfferLine[] = [];

export type AddValueAddedServicePayload = {
  offer: string;
  quantity: number;
};

@Injectable()
export class LocalCartService implements ICartService {
  config: ICartConfig;
  messageProductWithVasWasModified = 'CART.OFFER_LINE.ALERT.VAS_PRODUCT_MODIFIED';

  constructor(
    @Inject(CartConfigToken) private readonly providedConfig: ICartConfig,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly httpClient: HttpClient,
    private readonly ffService: ActiveFeaturesConfigcatService,
  ) {
    this.config = { ...DEFAULT_CONFIG, ...this.providedConfig };
  }

  read(accountType: PriceTypes): Observable<ICart | ShoppingCartError> {
    const cart = this.getCart();
    if (!cart.length) {
      return of({
        currency: '',
        orderNumber: '',
        items: [],
        totals: null,
        features: null,
        maxShippingTimeTotal: 0,
        minShippingTimeTotal: 0,
      });
    }

    const url = `${ApiConfig.SVC_APP_CART_URL_API_ENDPOINT()}/api/public/v1/cart`;

    const items = cart.map((productLine: IOfferLine) => ({
      quantity: productLine.quantity,
      offer: productLine.offer,
      trackingData: productLine.trackingData,
      valueAddedServices: productLine?.valueAddedServices?.map((vas) => ({
        offer: vas.id,
        quantity: productLine.quantity,
        type: vas?.type,
      })),
    }));

    return this.httpClient.post(url, { items, accountType }).pipe(
      map((response) => response as ICart),
      catchError(handleError)
    );
  }

  addProduct(
    offer: string,
    productId: string,
    quantity: number,
    valueAddedServices: AddValueAddedServicePayload[],
    trackingData: TrackingData,
    accountType: PriceTypes
  ): Observable<IOfferLine | ShoppingCartError> {
    const cart: IOfferLine[] = this.getCart();

    const index = cart.findIndex((item: IOfferLine) => item.offer === offer);
    let existingValueAddedServices = [];

    if (index !== -1) {
      quantity += Number(cart[index].quantity);
      existingValueAddedServices = (cart[index].valueAddedServices || []).map(({ id, quantity }) => ({
        offer: id,
        quantity,
      }));
    }

    if (existingValueAddedServices?.length || (valueAddedServices?.length && cart[index]?.quantity > 0)) {
      CartCommonUtil.saveValueAddedServiceMessageToSessionStorage(this.messageProductWithVasWasModified);
    }

    const combinedValueAddedServices = this.combineValueAddedServiceWithExisting(
      valueAddedServices || [],
      existingValueAddedServices
    );

    return this.checkInventory(offer, productId, quantity, accountType, trackingData, combinedValueAddedServices).pipe(
      tap((addedProduct: IOfferLine) => {
        if (index === -1) {
          cart.push(addedProduct);
        } else {
          cart.splice(index, 1, addedProduct);
        }
        this.saveCart(cart);
      })
    );
  }

  updateQuantity(
    cartLine: CartOfferLine,
    quantity: number,
    accountType: PriceTypes
  ): Observable<IOfferLine | ShoppingCartError> {
    const cart = this.getCart();
    const index = cart.findIndex((p: IOfferLine) => p.offer === cartLine.offer);
    const valueAddedServices = cart[index]?.valueAddedServices?.map((vas) => ({ offer: vas.id, quantity }));

    if (index === -1) {
      const errorMessage = 'No product found to update'; // TODO: Add translation
      const error = new ShoppingCartError(errorMessage, {
        common: { reason: errorMessage },
      });

      return throwError(error);
    }

    return this.checkInventory(cartLine.offer, cartLine.item.id, quantity, accountType, cartLine.trackingData, valueAddedServices).pipe(
      tap((productLine: IOfferLine) => {
        cart.splice(index, 1, productLine);
        this.saveCart(cart);
      })
    );
  }

  deleteProduct(
    offer: string,
    cartLineId: string,
    hasToStoreNotification: boolean
  ): Observable<ICartSuccess | ShoppingCartError> {
    const cart = this.getCart();
    const index = cart.findIndex((p: IOfferLine) => p.offer === offer);

    if (index === -1) {
      const errorMessage = 'No product found to delete'; // TODO: Add translation
      const error = new ShoppingCartError(errorMessage, {
        common: { reason: errorMessage },
      });

      return throwError(error);
    }

    cart.splice(index, 1);
    this.saveCart(cart);

    return of(new ICartSuccess());
  }

  deleteValueAddedService(vasId: string, offerId: string): Observable<ICartSuccess | ShoppingCartError> {
    const cart = this.getCart();
    const offerIndex = cart.findIndex((p: IOfferLine) => p.offer === offerId);

    if (offerIndex === -1) {
      const errorMessage = 'No product found to delete'; // TODO: Add translation
      const error = new ShoppingCartError(errorMessage, {
        common: { reason: errorMessage },
      });

      return throwError(error);
    }

    const valueAddedServiceIndex = cart[offerIndex].valueAddedServices.findIndex((vas) => vas.id === vasId);

    if (valueAddedServiceIndex === -1) {
      const errorMessage = 'No Value added service found to delete';
      const error = new ShoppingCartError(errorMessage, {
        common: { reason: errorMessage },
      });

      return throwError(error);
    }

    cart[offerIndex].valueAddedServices = cart[offerIndex].valueAddedServices.filter((vas) => vas.id !== vasId);
    this.saveCart(cart);

    return of(new ICartSuccess());
  }

  checkout(force: boolean): Observable<ICart | ShoppingCartError> {
    return throwError(new Error('User can not proceed to checkout from local cart'));
  }

  clean() {
    this.saveCart(EMPTY_CART);
  }

  getCart(): IOfferLine[] {
    if (isPlatformServer(this.platformId)) {
      return [];
    }

    const serializedCart = localStorage.getItem(this.config.cartLocalStorageName);
    return this.deserializeCart(serializedCart);
  }

  getItemsInCartCount(): Observable<number | null> {
    const cart = this.getCart();
    if (cart) {
      return of(cart.reduce((acc, item) => acc + +item.quantity, 0));
    }
    return of(null);
  }

  private saveCart(cart: IOfferLine[]): void {
    const serializedCart = JSON.stringify(cart);
    localStorage.setItem(this.config.cartLocalStorageName, serializedCart);
  }

  private deserializeCart(serializedCart: string): IOfferLine[] {
    let cart: IOfferLine[] = [...EMPTY_CART];

    if (serializedCart) {
      try {
        cart = JSON.parse(serializedCart);
      } catch (error) {
        console.error('Error on cart deserialization', error);
      }
    }

    return cart;
  }

  private checkInventory(
    offer: string,
    productId: string,
    quantity: number,
    accountType: PriceTypes,
    trackingData?: TrackingData,
    valueAddedServices?: { offer: string; quantity: number }[]
  ): Observable<IOfferLine | ShoppingCartError> {
    const isFFAddToCartOnAppCartEnabled$ = this.ffService.isFeatureEnabledAndReadyV2(FF_ADD_TO_CART_ON_APP_CART);

    return isFFAddToCartOnAppCartEnabled$.pipe(
      switchMap((isAppCartEnabled) => {
        if (isAppCartEnabled) {
          const url = `${ApiConfig.SVC_APP_CART_URL_API_ENDPOINT()}/api/public/v2/cart`;
          const cartLineId = uuidv4();

          return this.httpClient
            .post(url, {
              accountType,
              items: [
                {
                  cartLineId,
                  offer,
                  item: productId,
                  quantity,
                  trackingData,
                  services: valueAddedServices
                }
              ],
            })
            .pipe(
              catchError(handleError),
              switchMap((response: { items: IOfferLine[] }) => {
                const cartLine = {
                  ...response.items[0],
                  valueAddedServices: response.items[0]?.services || [],
                };

                if (cartLine?.invalidParams?.length > 0) {
                  const invalidParam = cartLine.invalidParams[0];
                  return throwError(new ShoppingCartError('', {[invalidParam.reason]: invalidParam}))
                }

                return of(cartLine);
              }),
            );
        } else {
          const url = `${this.config.checkInventoryApiUrl}/${offer}`;

          return this.httpClient
            .post(url, {
              quantity,
              valueAddedServices,
              trackingData,
              accountType,
            })
            .pipe(
              map((sellerOffer: IOfferLine) => sellerOffer),
              catchError(handleError)
            );
        }
      })
    );
  }

  private combineValueAddedServiceWithExisting(
    valueAddedServices: AddValueAddedServicePayload[],
    existingValueAddedServices: AddValueAddedServicePayload[]
  ): AddValueAddedServicePayload[] {
    return Object.values(
      valueAddedServices.concat(existingValueAddedServices).reduce(
        (servicesMap, currentService) =>
          servicesMap[currentService.offer]
            ? {
                ...servicesMap,
                [currentService.offer]: {
                  offer: currentService.offer,
                  quantity: servicesMap[currentService.offer].quantity + currentService.quantity,
                },
              }
            : {
                ...servicesMap,
                [currentService.offer]: currentService,
              },
        {}
      )
    );
  }
}
